import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Mail, Phone, ArrowRight, Users, Trophy, Clock } from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const HOW_ICONS = [Users, Trophy, Clock];

const Kontakt = () => {
  const { t } = useTranslation();
  const howItems = t("kontakt.how_items", { returnObjects: true });
  const priceItems = t("kontakt.price_items", { returnObjects: true });

  return (
    <section
      id="kontakt"
      data-testid="kontakt-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="rich" />

      <div className="relative mx-auto max-w-[1300px] px-5 md:px-10">
        <div className="text-center">
          <span className="eyebrow">{t("kontakt.eyebrow")}</span>
          <h2 className="font-display mt-5 text-xl sm:text-[1.5rem] lg:text-[1.75rem] font-semibold leading-[1.18] text-white glow-text-white uppercase tracking-[-0.005em]">
            {t("kontakt.title")}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
            {t("kontakt.lead")}
          </p>
        </div>

        {/* Contact card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-12 grid gap-5 md:grid-cols-2"
        >
          <a
            href="mailto:ds@elektrouniversal.dk?subject=Future%20Football%20-%20Skr%C3%A6ddersyet%20forl%C3%B8b"
            data-testid="kontakt-mail-card"
            className="group relative flex items-center gap-5 overflow-hidden rounded-2xl glass-strong p-7 electric-border transition-transform hover:-translate-y-1"
          >
            <div
              aria-hidden
              className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-50 blur-3xl"
              style={{ background: "#FF3DCB" }}
            />
            <div className="relative flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF2D5C]/30 to-transparent border border-[#FF3DCB]/30">
              <Mail className="h-6 w-6 text-[#FF3DCB]" />
            </div>
            <div className="relative flex-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">
                {t("kontakt.mail_label")}
              </span>
              <div className="font-display mt-1 text-lg md:text-xl font-semibold text-white break-all">
                ds@elektrouniversal.dk
              </div>
            </div>
            <ArrowRight className="relative h-5 w-5 text-white/65 transition-transform group-hover:translate-x-1 group-hover:text-white" />
          </a>

          <a
            href="tel:+4560439162"
            data-testid="kontakt-phone-card"
            className="group relative flex items-center gap-5 overflow-hidden rounded-2xl glass-strong p-7 electric-border transition-transform hover:-translate-y-1"
          >
            <div
              aria-hidden
              className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-50 blur-3xl"
              style={{ background: "#3DBFFF" }}
            />
            <div className="relative flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#3DBFFF]/30 to-transparent border border-[#3DBFFF]/30">
              <Phone className="h-6 w-6 text-[#3DBFFF]" />
            </div>
            <div className="relative flex-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">
                {t("kontakt.phone_label")}
              </span>
              <div className="font-display mt-1 text-lg md:text-xl font-semibold text-white">
                +45 60 43 91 62
              </div>
            </div>
            <ArrowRight className="relative h-5 w-5 text-white/65 transition-transform group-hover:translate-x-1 group-hover:text-white" />
          </a>
        </motion.div>

        {/* How we work */}
        <div className="mt-16">
          <h3 className="font-display text-center text-2xl md:text-3xl font-bold text-white">
            {t("kontakt.how_title")}
          </h3>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {Array.isArray(howItems) &&
              howItems.map((it, i) => {
                const Icon = HOW_ICONS[i % HOW_ICONS.length];
                return (
                  <div
                    key={i}
                    data-testid={`kontakt-how-${i}`}
                    className="glass rounded-2xl p-6 electric-border"
                  >
                    <Icon className="h-5 w-5 text-[#6FF6FF]" />
                    <h4 className="font-display mt-4 text-lg font-semibold text-white">
                      {it.h}
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-white/65">
                      {it.p}
                    </p>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Price */}
        <div className="mt-12 grid gap-5 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="eyebrow">{t("kontakt.price_title")}</span>
            <p className="mt-4 text-base text-white/70">{t("kontakt.price_lead")}</p>
          </div>
          <div className="md:col-span-7 grid grid-cols-3 gap-3">
            {Array.isArray(priceItems) &&
              priceItems.map((p, i) => (
                <div
                  key={i}
                  data-testid={`kontakt-price-${i}`}
                  className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-5 text-center"
                >
                  <span className="font-display number-badge text-2xl font-bold">
                    0{i + 1}
                  </span>
                  <p className="mt-2 font-display text-sm text-white">{p}</p>
                </div>
              ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-14 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <a
            href="mailto:ds@elektrouniversal.dk?subject=Future%20Football%20-%20Skr%C3%A6ddersyet%20forl%C3%B8b"
            data-testid="kontakt-cta-book"
            className="group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm md:text-base btn-neon-primary"
          >
            {t("kontakt.cta_book")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="tel:+4560439162"
            data-testid="kontakt-cta-call"
            className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm md:text-base btn-neon-ghost"
          >
            <Phone className="h-4 w-4" />
            {t("kontakt.cta_call")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default Kontakt;
